import { AxiosInstance, AxiosRequestConfig } from "axios";

const PERMITTED_METHOD = "get";

export type ReadClassification = {
  method: string;
  path: string;
};

// Endpoints that are reads despite not being GET. HTTP method is not mutation
// semantics: these carry a body because they carry a query.
//
// Classified by the backend owner, who confirmed none of them updates case
// information. Whether any writes an audit entry was asked and not answered;
// that is accepted, because an audit row harms no record. Adding to this list
// on frontend inference is not permitted — get it classified first.
const READ_CLASSIFICATIONS: readonly ReadClassification[] = Object.freeze([
  Object.freeze({ method: "post", path: "item_cases" }),
  Object.freeze({ method: "post", path: "search_cases" }),
  Object.freeze({ method: "post", path: "test/search_cases" }),
  // New Case duplicate check: a single parameterized SELECT on the Aurora case
  // table, written and classified by the backend owner.
  Object.freeze({ method: "post", path: "duplicate_check" }),
  // The endpoint is dead. It stays classified so the dashboard fails the way it
  // genuinely fails, rather than looking like this guard is the problem.
  Object.freeze({ method: "post", path: "dashboard_embed" }),
]);

const isProduction = () => process.env.NODE_ENV === "production";

const normalisePath = (url: string) => url.split("?")[0].replace(/^\/+/, "");

export function createDevWriteGuard(
  classifications: readonly ReadClassification[] = READ_CLASSIFICATIONS
) {
  return function devWriteGuard(config: AxiosRequestConfig): AxiosRequestConfig {
    if (isProduction()) return config;

    const method = (config.method ?? PERMITTED_METHOD).toLowerCase();
    if (method === PERMITTED_METHOD) return config;

    const path = normalisePath(config.url ?? "");
    const classified = classifications.some(
      (entry) => entry.method === method && entry.path === path
    );
    if (classified) return config;

    const message =
      `[dev-write-guard] blocked ${method.toUpperCase()} ${path} — it is not ` +
      `classified as a read, so it is treated as a write. This backend is ` +
      `production. If this endpoint does not mutate, have it classified rather ` +
      `than working around this.`;

    // Logged before throwing: a caller's catch must not be able to hide it.
    console.error(message);
    throw new Error(message);
  };
}

export const devWriteGuard = createDevWriteGuard();

const guardedInstances = new WeakSet<AxiosInstance>();

export function installDevWriteGuard(instance: AxiosInstance): boolean {
  if (guardedInstances.has(instance)) return false;
  guardedInstances.add(instance);
  instance.interceptors.request.use(devWriteGuard);
  return true;
}
